import { useState, type ChangeEvent, type KeyboardEvent, type PointerEvent } from "react";
import { deleteNode } from "../../api/commands.js";
import { DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT } from "../../constants.js";
import { useConfirm } from "../shell/ConfirmProvider.js";
import type { NodeWithWriting } from "./MapEditingContext.js";
import type { NodeSize } from "./MapEditingContext.js";
import { Button, useT } from "@mmm/ui";
interface NodeInspectorProps {
  node: NodeWithWriting;
  onClose: () => void;
  onPreviewSize: (size: NodeSize) => void;
  onCommitSize: (size: NodeSize) => void;
  variant?: "canvas" | "sidebar";
}

// Compact inspector: size and delete. Title editing happens directly on the
// node via double-click; the sidebar stays focused on structural controls.
export function NodeInspector({ node, onClose, onPreviewSize, onCommitSize, variant = "canvas" }: NodeInspectorProps) {
  const confirm = useConfirm();
  const t = useT();
  const nodeSizeMultiplier = Math.round((node.width / DEFAULT_NODE_WIDTH) * 10) / 10;
  const [sizeMultiplier, setSizeMultiplier] = useState(nodeSizeMultiplier);

  const dimensionsForMultiplier = (multiplier: number): NodeSize => ({
    width: Math.round(DEFAULT_NODE_WIDTH * multiplier),
    height: Math.round(DEFAULT_NODE_HEIGHT * multiplier),
  });

  const handleSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const multiplier = Number(event.target.value);
    setSizeMultiplier(multiplier);
    onPreviewSize(dimensionsForMultiplier(multiplier));
  };

  const commitSize = (event: PointerEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) => {
    onCommitSize(dimensionsForMultiplier(Number(event.currentTarget.value)));
  };

  const handleSizeKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"].includes(event.key)) {
      commitSize(event);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t("maps.deleteNode"),
      message: t("maps.deleteNodeMessage", { title: node.title }),
      confirmLabel: t("maps.deleteNode"),
    });
    if (!ok) return;
    try {
      await deleteNode({ nodeId: node.id });
      onClose();
    } catch {
      // Already toasted — the Button's onClick invokes this without awaiting it.
    }
  };

  return (
    <div className={`mmm-node-inspector mmm-node-inspector--${variant}`.trim()}>
      {variant !== "sidebar" && (
        <div className="mmm-node-inspector-head">
          <span>{node.title}</span>
        </div>
      )}

      {variant !== "sidebar" && <label className="mmm-field-label">{t("maps.size")}</label>}
      <input
        aria-label={t("maps.nodeSize")}
        type="range"
        min="0.8"
        max="1.8"
        step="0.1"
        value={sizeMultiplier}
        onChange={handleSizeChange}
        onPointerUp={commitSize}
        onPointerCancel={commitSize}
        onKeyUp={handleSizeKeyUp}
        style={{ width: "100%", accentColor: "var(--mmm-purple)" }}
      />

      <Button
        variant="danger"
        className="mmm-node-delete-btn"
        onClick={handleDelete}
      >
        {t("maps.deleteNode")}
      </Button>
    </div>
  );
}
